import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ArrowRight, Check, Upload, X, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Configurazione minimo caratteri descrizione
const MIN_DESCRIPTION_LENGTH = 50;

// Helper function to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove the data:*/*;base64, prefix
      const base64String = reader.result.split(',')[1];
      resolve(base64String);
    };
    reader.onerror = (error) => reject(error);
  });
};

function CreateIssueWizard({ onClose, onSuccess }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    project: '',
    description: '',
    tag: '',
    phone: '',
    email: '',
    impact: '',
    criticality: '',
    extension: '',
    attachments: []
  });

  const [generatedTitle, setGeneratedTitle] = useState('');
  const [errors, setErrors] = useState({});
  
  // Filter projects based on search
  const filteredProjects = projects.filter(p => 
    p.toLowerCase().includes(projectSearch.toLowerCase())
  );

  // Check authentication on mount
  useEffect(() => {
    const sessionToken = localStorage.getItem('customer_session');
    if (!sessionToken) {
      toast.error('Sessione non valida. Effettua il login.');
      navigate('/customer');
      return;
    }
    fetchProjects();
  }, [navigate]);

  useEffect(() => {
    if (formData.project) {
      fetchTags(formData.project);
    }
  }, [formData.project]);

  const fetchProjects = async () => {
    try {
      const sessionToken = localStorage.getItem('customer_session');
      if (!sessionToken) {
        navigate('/customer');
        return;
      }
      const response = await axios.get(`${BACKEND_URL}/api/customer/projects`, {
        params: { session_token: sessionToken }
      });
      setProjects(response.data.projects || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('customer_session');
        localStorage.removeItem('customer_user');
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        navigate('/customer');
      } else {
        toast.error('Errore nel caricamento dei progetti');
      }
    }
  };

  const fetchTags = async (projectName) => {
    try {
      const sessionToken = localStorage.getItem('customer_session');
      if (!sessionToken) {
        navigate('/customer');
        return;
      }
      const response = await axios.get(`${BACKEND_URL}/api/customer/tags`, {
        params: { 
          session_token: sessionToken,
          project: projectName
        }
      });
      setTags(response.data.tags || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('customer_session');
        localStorage.removeItem('customer_user');
        toast.error('Sessione scaduta. Effettua nuovamente il login.');
        navigate('/customer');
      } else {
        toast.error('Errore nel caricamento dei tag');
      }
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.project) newErrors.project = 'Seleziona un progetto';
    }

    if (step === 2) {
      if (!formData.description || formData.description.length < MIN_DESCRIPTION_LENGTH) {
        newErrors.description = `La descrizione deve contenere almeno ${MIN_DESCRIPTION_LENGTH} caratteri`;
      }
    }

    // Step 3: Tag is optional (free text)
    // No validation needed for tag

    if (step === 4) {
      if (!formData.phone) {
        newErrors.phone = 'Il numero di telefono è obbligatorio';
      } else if (!/^[0-9+\s()-]{8,}$/.test(formData.phone)) {
        newErrors.phone = 'Numero di telefono non valido';
      }
    }

    if (step === 5) {
      if (!formData.impact) newErrors.impact = 'Seleziona il livello di impatto';
      if (!formData.criticality) newErrors.criticality = 'Seleziona il livello di criticità';
      if (!formData.extension) newErrors.extension = 'Seleziona il livello di estensione';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData({
      ...formData,
      attachments: [...formData.attachments, ...files]
    });
  };

  const removeAttachment = (index) => {
    const newAttachments = formData.attachments.filter((_, i) => i !== index);
    setFormData({ ...formData, attachments: newAttachments });
  };

  const generateTitle = async () => {
    // Generate title using AI
    try {
      const sessionToken = localStorage.getItem('customer_session');
      const response = await axios.post(`${BACKEND_URL}/api/customer/generate-title`, {
        session_token: sessionToken,
        description: formData.description
      });
      
      if (response.data.success) {
        setGeneratedTitle(response.data.title);
        if (response.data.fallback) {
          console.log('Used fallback title generation');
        }
      }
    } catch (err) {
      console.error('AI title generation error:', err);
      // Fallback: use first sentence
      const desc = formData.description.trim();
      let title = desc.split('.')[0];
      if (title.length > 80) {
        title = desc.substring(0, 77) + '...';
      }
      setGeneratedTitle(title);
    }
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      if (currentStep === 6) {
        // Generate title using AI before showing summary (step 7)
        setLoading(true);
        await generateTitle();
        setLoading(false);
      }
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);

    try {
      const sessionToken = localStorage.getItem('customer_session');
      
      // Ensure we have a title - generate from description if empty
      let title = generatedTitle?.trim();
      if (!title) {
        const desc = formData.description.trim();
        title = desc.split('.')[0];
        if (title.length > 80) {
          title = desc.substring(0, 77) + '...';
        }
        if (!title) {
          title = 'Nuova segnalazione';
        }
      }
      
      // Prepare additional_information
      const additionalInfo = `
Contatto:
- Telefono: ${formData.phone}
${formData.email ? `- Email: ${formData.email}` : ''}

Livelli:
- Impatto: ${formData.impact}
- Criticità: ${formData.criticality}
- Estensione: ${formData.extension}
      `.trim();

      // Convert files to base64
      const filesBase64 = [];
      for (const file of formData.attachments) {
        try {
          const base64Content = await fileToBase64(file);
          filesBase64.push({
            name: file.name,
            content: base64Content
          });
        } catch (err) {
          console.error('Error converting file to base64:', err);
        }
      }

      const dataToSend = {
        session_token: sessionToken,
        project: formData.project,
        summary: title,
        description: formData.description,
        tag: formData.tag || '',
        additional_information: additionalInfo,
        impact: formData.impact,
        criticality: formData.criticality,
        extension: formData.extension,
        files: filesBase64
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/customer/create-issue`,
        dataToSend,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(`Segnalazione #${response.data.issue_id} creata con successo!`);
      
      if (onSuccess) {
        onSuccess(response.data.issue_id);
      } else {
        navigate('/customer');
      }
      
    } catch (err) {
      // Handle error - ensure it's always a string
      let errorMessage = 'Errore nella creazione della segnalazione';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Validation errors from FastAPI
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      console.error('Create issue error:', err.response?.data);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelect = (projectName) => {
    setFormData({...formData, project: projectName, tag: ''});
    setProjectSearch(projectName);
    setShowProjectDropdown(false);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)] mb-4">
              Seleziona Progetto
            </h2>
            <div className="relative">
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Progetto *
              </label>
              <Input
                value={projectSearch}
                onChange={(e) => {
                  setProjectSearch(e.target.value);
                  setShowProjectDropdown(true);
                  // Clear selection if user is typing something different
                  if (formData.project && e.target.value !== formData.project) {
                    setFormData({...formData, project: '', tag: ''});
                  }
                }}
                onFocus={() => setShowProjectDropdown(true)}
                placeholder="Cerca un progetto..."
                className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
              />
              
              {/* Autocomplete dropdown */}
              {showProjectDropdown && filteredProjects.length > 0 && (
                <div className="absolute z-50 w-full mt-1 max-h-60 overflow-auto bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded-md shadow-lg">
                  {filteredProjects.map(project => (
                    <div
                      key={project}
                      onClick={() => handleProjectSelect(project)}
                      className={`px-4 py-2 cursor-pointer font-mono text-sm hover:bg-[rgb(59,130,246)] hover:text-white transition-colors ${
                        formData.project === project ? 'bg-[rgb(59,130,246)] text-white' : 'text-[rgb(250,250,250)]'
                      }`}
                    >
                      {project}
                    </div>
                  ))}
                </div>
              )}
              
              {/* Selected project indicator */}
              {formData.project && (
                <p className="text-green-500 text-sm font-mono mt-2">
                  ✓ Progetto selezionato: {formData.project}
                </p>
              )}
              
              {errors.project && (
                <p className="text-red-500 text-sm font-mono mt-1">{errors.project}</p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)] mb-4">
              Descrizione Problema
            </h2>
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Descrizione * (minimo {MIN_DESCRIPTION_LENGTH} caratteri)
              </label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Descrivi il problema in dettaglio..."
                rows={8}
                className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-[rgb(113,113,122)] font-mono">
                  {formData.description.length} / {MIN_DESCRIPTION_LENGTH} caratteri
                </p>
                {errors.description && (
                  <p className="text-red-500 text-sm font-mono">{errors.description}</p>
                )}
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)] mb-4">
              Tag (Opzionale)
            </h2>
            <p className="text-sm text-[rgb(161,161,170)] font-mono mb-4">
              Puoi inserire un tag per categorizzare la segnalazione. Tutte le segnalazioni avranno categoria "Assistenza Tecnica".
            </p>
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Tag
              </label>
              {/* Show suggestions from configured tags */}
              {tags.length > 0 && (
                <div className="mb-3">
                  <p className="text-xs text-[rgb(113,113,122)] font-mono mb-2">Tag suggeriti:</p>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => (
                      <button
                        key={tag}
                        type="button"
                        onClick={() => setFormData({...formData, tag: tag})}
                        className={`px-3 py-1 rounded-full text-sm font-mono transition-colors ${
                          formData.tag === tag
                            ? 'bg-[rgb(59,130,246)] text-white'
                            : 'bg-[rgb(39,39,42)] text-[rgb(161,161,170)] hover:bg-[rgb(63,63,70)]'
                        }`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <Input
                value={formData.tag}
                onChange={(e) => setFormData({...formData, tag: e.target.value})}
                placeholder="Inserisci un tag o lascia vuoto"
                className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
              />
              <p className="text-xs text-[rgb(113,113,122)] font-mono mt-2">
                Puoi scegliere un tag suggerito o inserire un testo libero
              </p>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)] mb-4">
              Dati Contatto
            </h2>
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Telefono *
              </label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="+39 123 456 7890"
                className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
              />
              {errors.phone && (
                <p className="text-red-500 text-sm font-mono mt-1">{errors.phone}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Email (opzionale)
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="email@esempio.com"
                className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
              />
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)] mb-4">
              Livelli di Priorità
            </h2>
            
            {/* Impatto */}
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Livello di Impatto *
              </label>
              <Select 
                value={formData.impact} 
                onValueChange={(value) => setFormData({...formData, impact: value})}
              >
                <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                  <SelectValue placeholder="Seleziona impatto" />
                </SelectTrigger>
                <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                  <SelectItem value="singolo_utente" className="font-mono">Singolo Utente</SelectItem>
                  <SelectItem value="gruppo_utenti" className="font-mono">Gruppo di Utenti</SelectItem>
                  <SelectItem value="tutti_utenti" className="font-mono">Tutti gli Utenti</SelectItem>
                </SelectContent>
              </Select>
              {errors.impact && (
                <p className="text-red-500 text-sm font-mono mt-1">{errors.impact}</p>
              )}
            </div>

            {/* Criticità */}
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Livello di Criticità *
              </label>
              <Select 
                value={formData.criticality} 
                onValueChange={(value) => setFormData({...formData, criticality: value})}
              >
                <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                  <SelectValue placeholder="Seleziona criticità" />
                </SelectTrigger>
                <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                  <SelectItem value="posso_continuare" className="font-mono">Posso Continuare a Lavorare</SelectItem>
                  <SelectItem value="non_posso_continuare" className="font-mono">Non Posso Continuare</SelectItem>
                </SelectContent>
              </Select>
              {errors.criticality && (
                <p className="text-red-500 text-sm font-mono mt-1">{errors.criticality}</p>
              )}
            </div>

            {/* Estensione */}
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Livello di Estensione *
              </label>
              <Select 
                value={formData.extension} 
                onValueChange={(value) => setFormData({...formData, extension: value})}
              >
                <SelectTrigger className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono">
                  <SelectValue placeholder="Seleziona estensione" />
                </SelectTrigger>
                <SelectContent className="bg-[rgb(39,39,42)] border-[rgb(63,63,70)]">
                  <SelectItem value="singolo_paziente" className="font-mono">Singolo Paziente</SelectItem>
                  <SelectItem value="gruppo_pazienti" className="font-mono">Gruppo di Pazienti</SelectItem>
                  <SelectItem value="tutti_pazienti" className="font-mono">Tutti i Pazienti</SelectItem>
                </SelectContent>
              </Select>
              {errors.extension && (
                <p className="text-red-500 text-sm font-mono mt-1">{errors.extension}</p>
              )}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-4">
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)] mb-4">
              Allegati
            </h2>
            <div>
              <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                Carica file o immagini (opzionale)
              </label>
              <div className="border-2 border-dashed border-[rgb(63,63,70)] rounded-lg p-6 text-center">
                <Upload className="h-12 w-12 text-[rgb(161,161,170)] mx-auto mb-4" />
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.txt"
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  capture="environment"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer text-[rgb(59,130,246)] hover:text-[rgb(37,99,235)] font-mono"
                >
                  Clicca per selezionare file
                </label>
                <p className="text-xs text-[rgb(113,113,122)] font-mono mt-2">
                  Immagini, PDF, DOC (max 10MB per file)
                </p>
              </div>

              {/* Lista allegati */}
              {formData.attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  {formData.attachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded p-3"
                    >
                      <span className="text-sm text-[rgb(250,250,250)] font-mono truncate">
                        {file.name}
                      </span>
                      <Button
                        onClick={() => removeAttachment(index)}
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-400"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <h2 className="text-xl font-bold font-mono text-[rgb(250,250,250)] mb-4">
              Riepilogo Segnalazione
            </h2>

            <div className="bg-[rgb(39,39,42)] border border-[rgb(63,63,70)] rounded-lg p-4 space-y-4">
              {/* Titolo Generato */}
              <div>
                <label className="block text-sm font-mono text-[rgb(161,161,170)] mb-2">
                  Titolo (generato automaticamente)
                </label>
                <Input
                  value={generatedTitle}
                  onChange={(e) => setGeneratedTitle(e.target.value)}
                  className="bg-[rgb(24,24,27)] border-[rgb(63,63,70)] text-[rgb(250,250,250)] font-mono"
                />
              </div>

              {/* Progetto */}
              <div>
                <span className="text-sm font-mono text-[rgb(113,113,122)]">Progetto: </span>
                <span className="text-sm font-mono text-[rgb(250,250,250)]">{formData.project}</span>
              </div>

              {/* Categoria fissa + Tag */}
              <div>
                <span className="text-sm font-mono text-[rgb(113,113,122)]">Categoria: </span>
                <span className="text-sm font-mono text-[rgb(250,250,250)]">Assistenza Tecnica</span>
              </div>
              {formData.tag && (
                <div>
                  <span className="text-sm font-mono text-[rgb(113,113,122)]">Tag: </span>
                  <span className="text-sm font-mono text-[rgb(250,250,250)]">{formData.tag}</span>
                </div>
              )}

              {/* Descrizione */}
              <div>
                <span className="text-sm font-mono text-[rgb(113,113,122)]">Descrizione: </span>
                <p className="text-sm font-mono text-[rgb(250,250,250)] mt-1 whitespace-pre-wrap">
                  {formData.description.substring(0, 200)}
                  {formData.description.length > 200 && '...'}
                </p>
              </div>

              {/* Contatto */}
              <div>
                <span className="text-sm font-mono text-[rgb(113,113,122)]">Telefono: </span>
                <span className="text-sm font-mono text-[rgb(250,250,250)]">{formData.phone}</span>
                {formData.email && (
                  <>
                    <br />
                    <span className="text-sm font-mono text-[rgb(113,113,122)]">Email: </span>
                    <span className="text-sm font-mono text-[rgb(250,250,250)]">{formData.email}</span>
                  </>
                )}
              </div>

              {/* Livelli */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs font-mono text-[rgb(113,113,122)]">Impatto</div>
                  <div className="text-sm font-mono text-[rgb(250,250,250)]">
                    {formData.impact.replace(/_/g, ' ')}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono text-[rgb(113,113,122)]">Criticità</div>
                  <div className="text-sm font-mono text-[rgb(250,250,250)]">
                    {formData.criticality.replace(/_/g, ' ')}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-mono text-[rgb(113,113,122)]">Estensione</div>
                  <div className="text-sm font-mono text-[rgb(250,250,250)]">
                    {formData.extension.replace(/_/g, ' ')}
                  </div>
                </div>
              </div>

              {/* Allegati */}
              {formData.attachments.length > 0 && (
                <div>
                  <span className="text-sm font-mono text-[rgb(113,113,122)]">
                    Allegati: {formData.attachments.length} file
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-[rgb(161,161,170)] font-mono text-sm">
              <AlertCircle className="h-4 w-4" />
              <span>Verifica i dati prima di procedere. Puoi tornare indietro per modificare.</span>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const totalSteps = 7;

  return (
    <div className="min-h-screen bg-[rgb(9,9,11)] py-8">
      <div className="max-w-3xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold font-mono text-[rgb(250,250,250)]">
            Nuova Segnalazione
          </h1>
          <Button
            onClick={onClose || (() => navigate('/customer'))}
            variant="ghost"
            className="text-[rgb(161,161,170)] hover:text-white font-mono"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-mono text-[rgb(161,161,170)]">
              Passo {currentStep} di {totalSteps}
            </span>
            <span className="text-sm font-mono text-[rgb(161,161,170)]">
              {Math.round((currentStep / totalSteps) * 100)}%
            </span>
          </div>
          <div className="h-2 bg-[rgb(39,39,42)] rounded-full overflow-hidden">
            <div
              className="h-full bg-[rgb(59,130,246)] transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-[rgb(24,24,27)] border border-[rgb(39,39,42)] rounded-lg p-6 mb-6">
          {renderStep()}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            onClick={handleBack}
            disabled={currentStep === 1}
            variant="outline"
            className="border-[rgb(63,63,70)] text-[rgb(161,161,170)] hover:text-white font-mono"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Indietro
          </Button>

          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              className="bg-[rgb(59,130,246)] hover:bg-[rgb(37,99,235)] text-white font-mono"
            >
              Avanti
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white font-mono"
            >
              {loading ? 'Invio in corso...' : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Conferma e Invia
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateIssueWizard;
